import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ArrowPathIcon,
  XMarkIcon,
  XCircleIcon,
  UserGroupIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  PlusIcon,
  AdjustmentsHorizontalIcon,
} from '@heroicons/react/24/outline';
import {
  getReceivableClients,
  getReceivableClient,
  updateReceivableClient,
  mergeReceivableClient,
  addReceivablePayment,
  deleteReceivablePayment,
  addReceivableAdjustment,
  deleteReceivableAdjustment,
  syncReceivables,
} from '../api/client';
import { useStores } from '../contexts/StoreContext';
import { useAuth } from '../contexts/AuthContext';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function localDate(d = new Date()) {
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function thisMonday() {
  const d = new Date();
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return localDate(d);
}

function usd(v) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  return `${n < 0 ? '−' : ''}$${Math.abs(n).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function bs(v) {
  if (v === null || v === undefined) return '—';
  return `Bs ${Number(v).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(iso) {
  return iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(2, 4)}` : '—';
}

function shortStore(name) {
  return (name || '').replace(/\s*C\.?\s*A\.?\s*$/i, '').replace(/^FARMACIA |^FARMAVIDA /i, '');
}

const PLAN_STATUS = {
  sin_saldo:         { label: 'Sin saldo',         cls: 'bg-gray-100 text-gray-500' },
  sin_plan:          { label: 'Sin plan',          cls: 'bg-gray-100 text-gray-700' },
  al_dia:            { label: 'Semana cubierta',   cls: 'bg-green-100 text-green-800' },
  por_cobrar_semana: { label: 'Cobrar esta semana', cls: 'bg-sky-100 text-sky-800' },
  vencido:           { label: 'Plan vencido',      cls: 'bg-red-100 text-red-700' },
  pagado:            { label: 'Pagado',            cls: 'bg-green-100 text-green-800' },
};

const METHODS = ['Efectivo USD', 'Efectivo Bs', 'Pago móvil', 'Transferencia Bs', 'Zelle', 'Descuento de nómina', 'Otro'];

function SummaryCard({ label, value, sub, colorClass }) {
  return (
    <div className={`rounded-xl border px-4 py-3 ${colorClass}`}>
      <p className="text-xs font-semibold uppercase tracking-wide opacity-60 mb-0.5">{label}</p>
      <p className="text-xl font-black">{value}</p>
      {sub && <p className="text-xs opacity-60 mt-0.5">{sub}</p>}
    </div>
  );
}

// ─── Modal de abono ──────────────────────────────────────────────────────────

function PaymentModal({ client, onClose, onSaved }) {
  const [form, setForm] = useState({
    date: localDate(), currency: 'USD', amount: '', exchange_rate: '',
    method: 'Efectivo USD', reference: '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await addReceivablePayment(client.key, {
        ...form,
        amount: Number(form.amount),
        exchange_rate: form.currency === 'VES' && form.exchange_rate ? Number(form.exchange_rate) : null,
      });
      onSaved();
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo guardar el abono.');
    } finally {
      setSaving(false);
    }
  };

  const input = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500';
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <h3 className="font-bold text-gray-900">Registrar abono</h3>
            <p className="text-xs text-gray-500">{client.name} · saldo {usd(client.saldo_usd)}</p>
          </div>
          <button onClick={onClose}><XMarkIcon className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Fecha</label>
              <input type="date" value={form.date} onChange={e => set('date', e.target.value)} className={input} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Moneda</label>
              <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm">
                {[['USD', 'USD'], ['VES', 'Bs']].map(([k, l]) => (
                  <button key={k} type="button" onClick={() => set('currency', k)}
                    className={`flex-1 px-3 py-2 font-semibold ${form.currency === k ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600'}`}>{l}</button>
                ))}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Monto ({form.currency === 'USD' ? '$' : 'Bs'})</label>
              <input type="number" min="0" step="0.01" value={form.amount} onChange={e => set('amount', e.target.value)} className={input} autoFocus />
            </div>
            {form.currency === 'VES' && (
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Tasa (opcional)</label>
                <input type="number" min="0" step="0.0001" value={form.exchange_rate} placeholder="la del día"
                  onChange={e => set('exchange_rate', e.target.value)} className={input} />
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Método</label>
            <select value={form.method} onChange={e => set('method', e.target.value)} className={input}>
              {METHODS.map(m => <option key={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Referencia</label>
            <input value={form.reference} onChange={e => set('reference', e.target.value)} className={input} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Notas</label>
            <input value={form.notes} onChange={e => set('notes', e.target.value)} className={input} />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t bg-gray-50 rounded-b-xl">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600">Cancelar</button>
          <button onClick={save} disabled={saving || !(Number(form.amount) > 0)}
            className="px-4 py-2 text-sm font-semibold bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 text-white rounded-lg">
            {saving ? 'Guardando…' : 'Guardar abono'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Modal de ajuste de saldo (solo admin) ───────────────────────────────────

const ADJ_MODES = [
  ['add', 'Sumar a la deuda'],
  ['sub', 'Restar a la deuda'],
  ['set', 'Dejar el saldo en'],
];

function AdjustmentModal({ client, onClose, onSaved }) {
  const [mode, setMode] = useState('add');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(localDate());
  const [reason, setReason] = useState('Deuda anterior a agosto 2026');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const n = Number(amount);
  const delta = amount === '' ? null
    : mode === 'add' ? n : mode === 'sub' ? -n : n - client.saldo_usd;
  const valid = delta !== null && !isNaN(delta) && Math.abs(delta) >= 0.01 && (mode === 'set' || n > 0) && reason.trim();

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await addReceivableAdjustment(client.key, { date, amount_usd: Math.round(delta * 100) / 100, reason });
      onSaved();
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo guardar el ajuste.');
    } finally {
      setSaving(false);
    }
  };

  const input = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500';
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <h3 className="font-bold text-gray-900">Ajustar saldo</h3>
            <p className="text-xs text-gray-500">{client.name} · saldo actual {usd(client.saldo_usd)}</p>
          </div>
          <button onClick={onClose}><XMarkIcon className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm">
            {ADJ_MODES.map(([k, l]) => (
              <button key={k} type="button" onClick={() => setMode(k)}
                className={`flex-1 px-2 py-2 font-semibold ${mode === k ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600'}`}>{l}</button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Monto ($)</label>
              <input type="number" min="0" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} className={input} autoFocus />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Fecha</label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)} className={input} />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Motivo</label>
            <input value={reason} onChange={e => setReason(e.target.value)} className={input} />
          </div>
          {valid && (
            <p className="text-sm text-gray-600">
              Ajuste de <strong className={delta > 0 ? 'text-red-700' : 'text-green-700'}>{delta > 0 ? '+' : ''}{usd(delta)}</strong>
              {' '}→ el saldo queda en <strong>{usd(client.saldo_usd + delta)}</strong>
            </p>
          )}
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t bg-gray-50 rounded-b-xl">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600">Cancelar</button>
          <button onClick={save} disabled={saving || !valid}
            className="px-4 py-2 text-sm font-semibold bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 text-white rounded-lg">
            {saving ? 'Guardando…' : 'Guardar ajuste'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Panel de detalle del cliente ────────────────────────────────────────────

function ClientPanel({ clientKey, onClose, onChanged, isAdmin, allClients }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [adjOpen, setAdjOpen] = useState(false);
  const [weeks, setWeeks] = useState('');
  const [startDate, setStartDate] = useState(thisMonday());
  const [notes, setNotes] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await getReceivableClient(clientKey);
      setData(d);
      setWeeks(d.client.plan?.weeks ? String(d.client.plan.weeks) : '');
      setStartDate(d.client.plan?.start_date || thisMonday());
      setNotes(d.client.notes || '');
      setPhone(d.client.phone || '');
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo cargar el cliente.');
    }
  }, [clientKey]);

  useEffect(() => { load(); }, [load]);

  const save = async (payload) => {
    setSaving(true);
    try {
      await updateReceivableClient(clientKey, payload);
      await load();
      onChanged();
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo guardar.');
    } finally {
      setSaving(false);
    }
  };

  const [mergeInto, setMergeInto] = useState('');
  const merge = async () => {
    const target = allClients.find(x => x.key === mergeInto);
    if (!target || !window.confirm(
      `¿Unificar "${data.client.name}" dentro de "${target.name}"? Sus compras y abonos pasan a ese cliente y la copia de Tecnodroid ya no los separa.`
    )) return;
    setSaving(true);
    try {
      await mergeReceivableClient(clientKey, mergeInto);
      onChanged();
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo unificar.');
      setSaving(false);
    }
  };

  const removePayment = async (id) => {
    if (!window.confirm('¿Eliminar este abono?')) return;
    await deleteReceivablePayment(id);
    await load();
    onChanged();
  };

  const removeAdjustment = async (id) => {
    if (!window.confirm('¿Eliminar este ajuste de saldo?')) return;
    await deleteReceivableAdjustment(id);
    await load();
    onChanged();
  };

  const c = data?.client;
  const plan = c?.plan || {};
  const w = Number(weeks);
  const preview = c && w > 0 ? c.saldo_usd / w : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 z-40 flex justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-3xl h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-start justify-between z-10">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{c?.name || 'Cargando…'}</h2>
            {c && <p className="text-xs text-gray-500">C.I./RIF {c.code} · {(c.stores || []).map(shortStore).join(', ')}</p>}
          </div>
          <button onClick={onClose}><XMarkIcon className="w-6 h-6 text-gray-400" /></button>
        </div>

        {error && <p className="m-6 text-sm text-red-600">{error}</p>}

        {c && (
          <div className="p-6 space-y-6">
            {/* Saldo */}
            <div className="grid grid-cols-3 gap-3">
              <SummaryCard label="Comprado a crédito" value={usd(c.cargos_usd)} sub={`${c.facturas} facturas${Math.abs(c.ajustes_usd || 0) >= 0.01 ? ` · ajustes ${c.ajustes_usd > 0 ? '+' : ''}${usd(c.ajustes_usd)}` : ''}`} colorClass="bg-gray-50 border-gray-200 text-gray-800" />
              <SummaryCard label="Abonado" value={usd(c.abonos_usd)} sub={c.last_payment ? `último ${fmtDate(c.last_payment)}` : 'sin abonos'} colorClass="bg-green-50 border-green-200 text-green-800" />
              <SummaryCard label="Saldo" value={usd(c.saldo_usd)}
                sub={c.days_without_payment != null ? `${c.days_without_payment} días sin abonar` : ''}
                colorClass={c.saldo_usd > 0.005 ? 'bg-red-50 border-red-200 text-red-800' : 'bg-green-50 border-green-200 text-green-800'} />
            </div>

            <div className="flex flex-wrap gap-2">
              <button onClick={() => setPayOpen(true)}
                className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-semibold">
                <PlusIcon className="w-4 h-4" /> Registrar abono
              </button>
              {isAdmin && (
                <button onClick={() => setAdjOpen(true)}
                  className="flex items-center gap-2 border border-gray-300 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg text-sm font-semibold">
                  <AdjustmentsHorizontalIcon className="w-4 h-4" /> Ajustar saldo
                </button>
              )}
            </div>

            {/* Plan de cobro */}
            <div className="border border-gray-200 rounded-xl p-4">
              <p className="text-sm font-bold text-gray-800 mb-1">Plan de cobro</p>
              {plan.weeks ? (
                <p className="text-sm text-gray-600 mb-3">
                  {plan.weeks} semanas desde el {fmtDate(plan.start_date)} · fecha meta {fmtDate(plan.due_date)}
                  {plan.status === 'vencido'
                    ? <> · <span className="text-red-600 font-semibold">vencido: cobrar todo el saldo</span></>
                    : plan.weeks_left > 0 && <> · faltan {plan.weeks_left} semanas · cuota <strong>{usd(plan.cuota_semanal)}</strong>, falta esta semana <strong>{usd(plan.pendiente_semana)}</strong></>}
                </p>
              ) : (
                <p className="text-sm text-gray-500 mb-3">Sin plan. Indica en cuántas semanas quieres cobrarle y el sistema calcula la cuota semanal.</p>
              )}
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Cobrar en</label>
                  <div className="flex items-center gap-2">
                    <input type="number" min="1" max="104" value={weeks} onChange={e => setWeeks(e.target.value)}
                      className="w-20 border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                    <span className="text-sm text-gray-600">semanas</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Desde</label>
                  <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
                <button disabled={saving || !(w > 0)} onClick={() => save({ plan: { weeks: w, start_date: startDate } })}
                  className="px-4 py-2 text-sm font-semibold bg-gray-800 hover:bg-gray-900 disabled:bg-gray-300 text-white rounded-lg">
                  Guardar plan
                </button>
                {plan.weeks && (
                  <button disabled={saving} onClick={() => save({ clear_plan: true })} className="px-3 py-2 text-sm text-red-600">Quitar plan</button>
                )}
              </div>
              {preview != null && c.saldo_usd > 0 && (
                <p className="text-xs text-gray-500 mt-2">
                  Con el saldo de hoy serían <strong>{usd(preview)}</strong> por semana. La cuota se recalcula sola si el cliente compra más o abona de más.
                </p>
              )}
            </div>

            {/* Datos */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Teléfono</label>
                <input value={phone} onChange={e => setPhone(e.target.value)} onBlur={() => phone !== (c.phone || '') && save({ phone })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Notas</label>
                <input value={notes} onChange={e => setNotes(e.target.value)} onBlur={() => notes !== (c.notes || '') && save({ notes })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={!!c.internal} disabled={saving} onChange={e => save({ internal: e.target.checked })} />
              Consumo interno (socio, familia o farmacia del grupo): se muestra aparte
            </label>

            {isAdmin && (
              <div className="flex flex-wrap items-end gap-2">
                <div className="flex-1 min-w-[240px]">
                  <label className="block text-xs font-semibold text-gray-500 mb-1">
                    Unificar con otro cliente (misma persona o empresa con otra cédula/RIF)
                  </label>
                  <select value={mergeInto} onChange={e => setMergeInto(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                    <option value="">Elegir el cliente que se queda…</option>
                    {allClients.filter(x => x.key !== clientKey)
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map(x => <option key={x.key} value={x.key}>{x.name} · {x.code}</option>)}
                  </select>
                </div>
                <button disabled={saving || !mergeInto} onClick={merge}
                  className="px-4 py-2 text-sm font-semibold border border-gray-300 hover:bg-gray-50 disabled:text-gray-300 rounded-lg">
                  Unificar
                </button>
              </div>
            )}

            {/* Estado de cuenta */}
            <div>
              <p className="text-sm font-bold text-gray-800 mb-2">Estado de cuenta</p>
              <div className="border border-gray-200 rounded-lg overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-gray-400 uppercase tracking-wide">
                    <tr>
                      <th className="px-3 py-2 text-left">Fecha</th>
                      <th className="px-3 py-2 text-left">Concepto</th>
                      <th className="px-3 py-2 text-right">Compra</th>
                      <th className="px-3 py-2 text-right">Abono</th>
                      <th className="px-3 py-2 text-right">Saldo</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {[...data.moves].reverse().map(m => {
                      const off = m.status !== 'activa';
                      return (
                        <tr key={`${m.type}-${m.id}`} className={off ? 'text-gray-300 line-through' : ''}>
                          <td className="px-3 py-1.5 whitespace-nowrap">{fmtDate(m.date)} {m.time}</td>
                          <td className="px-3 py-1.5">
                            {m.type === 'factura'
                              ? <>{m.document} · {shortStore(m.store_name)}{off && <span className="no-underline"> ({m.status})</span>}</>
                              : m.type === 'ajuste'
                              ? <span className="text-amber-700">Ajuste · {m.notes}</span>
                              : <span className="text-green-700">Abono · {m.method}{m.reference ? ` · ${m.reference}` : ''}{m.amount_ves ? ` · ${bs(m.amount_ves)}` : ''}</span>}
                          </td>
                          <td className="px-3 py-1.5 text-right font-mono">{m.type === 'factura' || (m.type === 'ajuste' && m.amount_usd > 0) ? usd(m.amount_usd) : ''}</td>
                          <td className="px-3 py-1.5 text-right font-mono text-green-700">{m.type === 'abono' ? usd(m.amount_usd) : m.type === 'ajuste' && m.amount_usd < 0 ? usd(-m.amount_usd) : ''}</td>
                          <td className="px-3 py-1.5 text-right font-mono font-semibold">{usd(m.balance_usd)}</td>
                          <td className="px-2">
                            {m.type === 'abono' && isAdmin && (
                              <button onClick={() => removePayment(m.id)} title="Eliminar abono"><TrashIcon className="w-4 h-4 text-gray-300 hover:text-red-500" /></button>
                            )}
                            {m.type === 'ajuste' && isAdmin && (
                              <button onClick={() => removeAdjustment(m.id)} title="Eliminar ajuste"><TrashIcon className="w-4 h-4 text-gray-300 hover:text-red-500" /></button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
      {payOpen && c && (
        <div onClick={e => e.stopPropagation()}>
          <PaymentModal client={c} onClose={() => setPayOpen(false)}
            onSaved={async () => { setPayOpen(false); await load(); onChanged(); }} />
        </div>
      )}
      {adjOpen && c && (
        <div onClick={e => e.stopPropagation()}>
          <AdjustmentModal client={c} onClose={() => setAdjOpen(false)}
            onSaved={async () => { setAdjOpen(false); await load(); onChanged(); }} />
        </div>
      )}
    </div>
  );
}

// ─── Componente principal ────────────────────────────────────────────────────

export default function AccountsReceivable() {
  const { stores } = useStores();
  const { isAdmin } = useAuth();
  const [tab, setTab] = useState('externos');
  const [storeId, setStoreId] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('con_saldo');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [openKey, setOpenKey] = useState(null);
  const [payClient, setPayClient] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await getReceivableClients({ internal: tab === 'internos', storeId: storeId || null }));
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al cargar las cuentas por cobrar.');
    } finally {
      setLoading(false);
    }
  }, [tab, storeId]);

  useEffect(() => { load(); }, [load]);

  const runSync = async () => {
    setSyncing(true);
    try {
      await syncReceivables();
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo actualizar desde Tecnodroid.');
    } finally {
      setSyncing(false);
    }
  };

  const clients = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.clients || []).filter(c => {
      if (q && !(`${c.name} ${c.code}`.toLowerCase().includes(q))) return false;
      const st = c.plan?.status;
      if (filter === 'con_saldo') return c.saldo_usd > 0.005;
      if (filter === 'semana') return st === 'por_cobrar_semana' || st === 'vencido';
      if (filter === 'sin_plan') return st === 'sin_plan';
      if (filter === 'vencido') return st === 'vencido';
      return true;
    });
  }, [data, search, filter]);

  const t = data?.totals || {};
  const FILTERS = [
    ['con_saldo', 'Con saldo'], ['semana', 'Cobrar esta semana'], ['sin_plan', 'Sin plan'],
    ['vencido', 'Plan vencido'], ['todos', 'Todos'],
  ];

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <UserGroupIcon className="w-7 h-7 text-indigo-600" />
            Cuentas por Cobrar
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Ventas con método "Crédito" en Tecnodroid. Los abonos se registran aquí y bajan el saldo global del cliente.
          </p>
        </div>
        <div className="text-right">
          <button onClick={runSync} disabled={syncing}
            className="flex items-center gap-2 border border-gray-300 bg-white hover:bg-gray-50 px-4 py-2 rounded-lg text-sm font-semibold text-gray-700">
            <ArrowPathIcon className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Actualizando…' : 'Actualizar desde Tecnodroid'}
          </button>
          {data?.sync_state?.last_run && (
            <p className="text-xs text-gray-400 mt-1">Última copia: {new Date(data.sync_state.last_run + 'Z').toLocaleString('es-VE')}</p>
          )}
        </div>
      </div>

      {/* Pestañas */}
      <div className="flex gap-1 mb-4 border-b border-gray-200">
        {[['externos', 'Por cobrar'], ['internos', 'Consumo interno']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === k ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {l}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-5 bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-3">
          <XCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      {/* Totales */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
          <SummaryCard label="Saldo por cobrar" value={usd(t.saldo_usd)} sub={`${t.clientes_con_saldo} clientes`} colorClass="bg-red-50 border-red-200 text-red-800" />
          <SummaryCard label="Cobrar esta semana" value={usd(t.cobrar_esta_semana)} sub="según los planes" colorClass="bg-sky-50 border-sky-200 text-sky-800" />
          <SummaryCard label="Planes vencidos" value={usd(t.vencidos_usd)} colorClass="bg-orange-50 border-orange-200 text-orange-800" />
          <SummaryCard label="Sin plan" value={usd(t.sin_plan_usd)} sub="saldo sin plan de cobro" colorClass="bg-gray-50 border-gray-200 text-gray-800" />
          <SummaryCard label="Cobrado este mes" value={usd(t.cobrado_mes_usd)} colorClass="bg-green-50 border-green-200 text-green-800" />
          <SummaryCard label="Clientes" value={(data.clients || []).length} sub="con historial de crédito" colorClass="bg-gray-50 border-gray-200 text-gray-800" />
        </div>
      )}

      {/* Filtros */}
      <div className="bg-white border border-gray-200 rounded-xl p-3 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative">
          <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar nombre o cédula"
            className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm w-64" />
        </div>
        <select value={storeId} onChange={e => setStoreId(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
          <option value="">Todas las tiendas</option>
          {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <div className="flex gap-1 flex-wrap">
          {FILTERS.map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)}
              className={`px-3 py-1 rounded-full text-xs font-semibold ${filter === k ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {l}
            </button>
          ))}
        </div>
        {loading && <ArrowPathIcon className="w-4 h-4 text-gray-400 animate-spin" />}
      </div>

      {/* Tabla de clientes */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs text-gray-400 uppercase tracking-wide border-b border-gray-200 bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left">Cliente</th>
              <th className="px-4 py-2 text-right">Saldo</th>
              <th className="px-4 py-2 text-left">Plan</th>
              <th className="px-4 py-2 text-right">Cuota semanal</th>
              <th className="px-4 py-2 text-right">Falta esta semana</th>
              <th className="px-4 py-2 text-left">Último abono</th>
              <th className="px-4 py-2 text-left">Última compra</th>
              <th />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {clients.map(c => {
              const st = PLAN_STATUS[c.plan?.status] || PLAN_STATUS.sin_plan;
              return (
                <tr key={c.key} onClick={() => setOpenKey(c.key)} className="hover:bg-gray-50 cursor-pointer">
                  <td className="px-4 py-2.5">
                    <p className="font-medium text-gray-900">{c.name}</p>
                    <p className="text-xs text-gray-400">{c.code} · {c.stores.map(shortStore).join(', ')}</p>
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono font-bold text-gray-900">{usd(c.saldo_usd)}</td>
                  <td className="px-4 py-2.5">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${st.cls}`}>{st.label}</span>
                    {c.plan?.weeks && <p className="text-xs text-gray-400 mt-0.5">{c.plan.weeks} sem · meta {fmtDate(c.plan.due_date)}</p>}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono">{c.plan?.cuota_semanal ? usd(c.plan.cuota_semanal) : <span className="text-gray-300">—</span>}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-sky-700 font-semibold">{c.plan?.pendiente_semana ? usd(c.plan.pendiente_semana) : <span className="text-gray-300">—</span>}</td>
                  <td className="px-4 py-2.5 text-xs">
                    {c.last_payment ? fmtDate(c.last_payment) : <span className="text-gray-400">nunca</span>}
                    {c.days_without_payment != null && (
                      <p className={c.days_without_payment > 30 ? 'text-red-600' : 'text-gray-400'}>{c.days_without_payment} días sin abonar</p>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-500">{fmtDate(c.last_sale)}</td>
                  <td className="px-4 py-2.5 text-right">
                    {c.saldo_usd > 0.005 && (
                      <button onClick={e => { e.stopPropagation(); setPayClient(c); }}
                        className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 whitespace-nowrap">+ Abono</button>
                    )}
                  </td>
                </tr>
              );
            })}
            {data && clients.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-400">No hay clientes con ese filtro.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {openKey && (
        <ClientPanel clientKey={openKey} isAdmin={isAdmin} allClients={data?.clients || []}
          onClose={() => setOpenKey(null)} onChanged={load} />
      )}
      {payClient && (
        <PaymentModal client={payClient} onClose={() => setPayClient(null)}
          onSaved={() => { setPayClient(null); load(); }} />
      )}
    </div>
  );
}
